import { Component } from '@angular/core';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { VendorService } from '../../../services/vendor.service';
import { Vendor } from '../../../models/vendor.model';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import {
  MenuButtonComponent,
  MenuButtonOption,
} from '../../../components/menu-button/menu-button.component';
import { Statuses } from '../../../models/status.enum';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatDialog } from '@angular/material/dialog';
import { VendorDenialReasonDialogComponent } from '../../../components/dialogs/vendor-denial-reason-dialog/vendor-denial-reason-dialog.component';

interface ReviewVendorForm {
  id: FormControl<string | null>;
  name: FormControl<string | null>;
  email: FormControl<string | null>;
  firstName: FormControl<string | null>;
  lastName: FormControl<string | null>;
  phone: FormControl<string | null>;
  studentAmbassador: FormControl<string | null>;
}

enum ButtonValues {
  Approve = 'APPROVE',
  Deny = 'DENY',
  Cancel = 'CANCEL',
}

@Component({
  selector: 'app-review',
  standalone: true,
  imports: [
    MatButtonModule,
    MatCardModule,
    RouterModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MenuButtonComponent,
  ],
  templateUrl: './vendor-review.component.html',
  styleUrl: './vendor-review.component.scss',
})
export class VendorReviewComponent {
  vendor?: Vendor;

  alreadyReviewed: boolean = false;

  reviewVendorForm = new FormGroup<ReviewVendorForm>({
    id: new FormControl(''),
    name: new FormControl(''),
    email: new FormControl(''),
    firstName: new FormControl(''),
    lastName: new FormControl(''),
    phone: new FormControl(''),
    studentAmbassador: new FormControl(''),
  });

  menuButtonOptions: MenuButtonOption[] = [
    {
      title: 'Approve',
      value: ButtonValues.Approve,
      icon: 'check',
      iconColor: '#259b24',
    },
    {
      title: 'Deny',
      value: ButtonValues.Deny,
      icon: 'block',
      iconColor: 'warn',
    },
    {
      title: 'Cancel',
      value: ButtonValues.Cancel,
    },
  ];

  menuOptionSelected: MenuButtonOption = this.menuButtonOptions[0];

  constructor(
    private route: ActivatedRoute,
    private vendorService: VendorService,
    private router: Router,
    private snackbar: MatSnackBar,
    private dialog: MatDialog
  ) {
    this.route.params.subscribe((params) => {
      if (params['id']) {
        this.getVendor(params['id']);
      }
    });
  }

  getVendor(id: string) {
    this.vendorService.getSingle(id).subscribe((vendor) => {
      this.vendor = vendor;

      if (
        this.vendor.status == Statuses.ACTIVE ||
        this.vendor.status == Statuses.INACTIVE
      ) {
        this.alreadyReviewed = true;
      }

      this.updateForm(vendor);
    });
  }

  updateForm(vendor: Vendor) {
    let phone = vendor.user?.userProfile?.phone ?? '';

    if (phone.length === 10) {
      phone =
        '(' +
        phone.substring(0, 3) +
        ') ' +
        phone.substring(3, 6) +
        '-' +
        phone.substring(6, 10);
    }

    this.reviewVendorForm.setValue({
      id: vendor.id ?? '',
      name: vendor.name,
      email: vendor.user?.email ?? '',
      firstName: vendor.user?.firstName ?? '',
      lastName: vendor.user?.lastName ?? '',
      phone: phone,
      studentAmbassador: vendor.studentAmbassador ?? '',
    });
  }

  onActionClicked() {
    // If Cancel, just go back to vendors
    if (this.menuOptionSelected.value == ButtonValues.Cancel) {
      this.router.navigateByUrl('/vendors');
      return;
    }

    let newStatus = Statuses.PENDING;

    // Else
    switch (this.menuOptionSelected.value) {
      case ButtonValues.Approve:
        newStatus = Statuses.ACTIVE;
        break;
      case ButtonValues.Deny:
        newStatus = Statuses.INACTIVE;
        break;
    }

    if (this.vendor) {
      const updatedVendor: Vendor = {
        ...this.vendor,
        ...{
          status: newStatus,
        },
      };

      if (this.menuOptionSelected.value == ButtonValues.Deny) {
        const denyDialog = this.dialog.open(VendorDenialReasonDialogComponent);

        denyDialog.afterClosed().subscribe((reason) => {
          if (reason && reason != '') {
            this.vendorService
              .approveOrDenyVendor({
                vendorId: this.vendor?.id ?? '',
                isApproved: false,
                denyReason: reason,
              })
              .subscribe({
                next: () => {
                  this.snackbar.open('Vendor successfully saved!', undefined, {
                    duration: 2500,
                  });

                  this.router.navigateByUrl('/vendors');
                },
                error: () => {
                  this.snackbar.open(
                    'Error saving vendor, please try again later!',
                    undefined,
                    {
                      duration: 2500,
                    }
                  );
                },
              });
          }
        });
      } else {
        this.vendorService
          .approveOrDenyVendor({
            vendorId: this.vendor?.id ?? '',
            isApproved: true,
          })
          .subscribe({
            next: () => {
              this.snackbar.open('Vendor successfully saved!', undefined, {
                duration: 2500,
              });

              this.router.navigateByUrl('/vendors');
            },
            error: () => {
              this.snackbar.open(
                'Error saving vendor, please try again later!',
                undefined,
                {
                  duration: 2500,
                }
              );
            },
          });
      }
    } else {
      // There was an error getting the vendor
      this.snackbar.open(
        'There is no vendor to save to, please try again later!',
        undefined,
        {
          duration: 2500,
        }
      );
    }
  }
}
